// LegalTermsView — admin edita e versiona os documentos legais (Termos,
// Privacidade, Cookies) sem deploy. [[project-legal-consent-system]]
//
// Fluxo: cada kind tem N versões. Só UMA fica "published". Rascunhos (draft)
// são editáveis; publicar arquiva a versão anterior e dispara re-aceite pra
// toda a base. Versões published/archived são somente leitura — pra mudar,
// cria-se uma nova versão (rascunho) a partir do texto atual.

import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertTriangle,
  Eye,
  FileText,
  Loader2,
  Plus,
  Save,
  Send,
  Trash2,
  Users
} from "lucide-react";
import { LegalDocViewer } from "@app/ui";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { api, type LegalDocument, type LegalRollout } from "./api";
import { openAlert, openConfirm } from "./ConfirmPromptModal";

type Kind = "terms" | "privacy" | "cookies";
const KINDS: Array<{ kind: Kind; label: string }> = [
  { kind: "terms", label: "Termos de Uso" },
  { kind: "privacy", label: "Política de Privacidade" },
  { kind: "cookies", label: "Política de Cookies" }
];

const STATUS_BADGE: Record<string, string> = {
  published:
    "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40",
  draft: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40",
  archived: "bg-muted text-muted-foreground border-border"
};
const STATUS_LABEL: Record<string, string> = {
  published: "Publicado",
  draft: "Rascunho",
  archived: "Arquivado"
};

export function LegalTermsView() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [byKind, setByKind] = useState<Record<string, LegalDocument[]>>({});
  const [activeKind, setActiveKind] = useState<Kind>("terms");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  // Campos editáveis (rascunho).
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [summary, setSummary] = useState("");

  // Rollout (disponibilidade).
  const [rolloutMode, setRolloutMode] = useState<LegalRollout["mode"]>("off");
  const [allowlistText, setAllowlistText] = useState("");
  const [savingRollout, setSavingRollout] = useState(false);
  const [rolloutMsg, setRolloutMsg] = useState<string | null>(null);

  const versions = useMemo(
    () =>
      (byKind[activeKind] ?? [])
        .slice()
        .sort((a, b) => b.version - a.version),
    [byKind, activeKind]
  );
  const selected = useMemo(
    () => versions.find((v) => v.id === selectedId) ?? null,
    [versions, selectedId]
  );
  const isDraft = selected?.status === "draft";

  async function load(keepSelection = false) {
    setPhase((p) => (p === "ready" ? p : "loading"));
    try {
      const { documents, rollout } = await api.legalList();
      setByKind(documents);
      setRolloutMode(rollout?.mode ?? "off");
      setAllowlistText((rollout?.allowlist ?? []).join("\n"));
      setPhase("ready");
      if (!keepSelection) {
        const list = (documents[activeKind] ?? [])
          .slice()
          .sort((a, b) => b.version - a.version);
        const pub = list.find((v) => v.status === "published");
        setSelectedId(pub?.id ?? list[0]?.id ?? null);
      }
    } catch (err) {
      setErrMsg(friendlyError(err));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Ao trocar de kind, seleciona a versão publicada (ou a mais recente).
  useEffect(() => {
    const pub = versions.find((v) => v.status === "published");
    setSelectedId(pub?.id ?? versions[0]?.id ?? null);
    setPreview(false);
  }, [activeKind]);

  // Sincroniza campos editáveis quando a seleção muda.
  useEffect(() => {
    setTitle(selected?.title ?? "");
    setContent(selected?.content ?? "");
    setSummary(selected?.summary ?? "");
    setPreview(false);
  }, [selectedId]);

  async function handleNewVersion() {
    const base = versions.find((v) => v.status === "published") ?? versions[0];
    setBusy(true);
    setErrMsg(null);
    try {
      const { document } = await api.legalSaveDraft({
        kind: activeKind,
        title: base?.title ?? KINDS.find((k) => k.kind === activeKind)!.label,
        content: base?.content ?? "",
        summary: ""
      });
      await load(true);
      setSelectedId(document.id);
    } catch (err) {
      setErrMsg(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveDraft() {
    if (!selected || !isDraft) return;
    setBusy(true);
    setErrMsg(null);
    try {
      await api.legalSaveDraft({
        id: selected.id,
        kind: activeKind,
        title: title.trim(),
        content: content.trim(),
        summary: summary.trim() || null
      });
      await load(true);
    } catch (err) {
      setErrMsg(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handlePublish() {
    if (!selected || !isDraft) return;
    const ok = await openConfirm({
      title: "Publicar esta versão?",
      message:
        "Ao publicar, esta versão passa a valer para todos. A versão publicada anterior é arquivada e TODOS os usuários serão obrigados a aceitar novamente no próximo acesso. Confirmar?",
      confirmLabel: "Publicar",
      cancelLabel: "Cancelar"
    });
    if (!ok) return;
    setBusy(true);
    setErrMsg(null);
    try {
      // Salva o rascunho antes de publicar pra garantir o texto atual.
      await api.legalSaveDraft({
        id: selected.id,
        kind: activeKind,
        title: title.trim(),
        content: content.trim(),
        summary: summary.trim() || null
      });
      await api.legalPublish(selected.id);
      await load(true);
      await openAlert({
        title: "Publicado",
        message: "A nova versão está no ar e o re-aceite foi disparado."
      });
    } catch (err) {
      setErrMsg(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!selected || !isDraft) return;
    const ok = await openConfirm({
      title: "Apagar rascunho?",
      message: "Esta ação não pode ser desfeita.",
      confirmLabel: "Apagar",
      cancelLabel: "Cancelar",
      danger: true
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.legalDeleteDraft(selected.id);
      await load();
    } catch (err) {
      setErrMsg(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveRollout(nextMode?: LegalRollout["mode"]) {
    const mode = nextMode ?? rolloutMode;
    // Confirmação extra ao abrir pra TODOS (força re-aceite de toda a base).
    if (mode === "all") {
      const ok = await openConfirm({
        title: "Liberar para todos?",
        message:
          "O aceite de Termos e Privacidade passará a ser exigido de TODOS os usuários no próximo acesso, e o banner de cookies aparecerá para todos. Confirmar?",
        confirmLabel: "Liberar para todos",
        cancelLabel: "Cancelar"
      });
      if (!ok) return;
    }
    setSavingRollout(true);
    setRolloutMsg(null);
    try {
      const emails = allowlistText
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const { rollout } = await api.legalSetRollout(mode, emails);
      setRolloutMode(rollout.mode);
      setAllowlistText(rollout.allowlist.join("\n"));
      setRolloutMsg("Salvo.");
      setTimeout(() => setRolloutMsg(null), 1500);
    } catch (err) {
      setRolloutMsg(friendlyError(err));
    } finally {
      setSavingRollout(false);
    }
  }

  if (phase === "loading") {
    return (
      <div class="flex items-center gap-2 p-8 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando documentos…
      </div>
    );
  }

  return (
    <div class="space-y-5">
      <header class="flex items-center gap-2">
        <FileText className="size-5 text-violet-500" />
        <div>
          <h1 class="text-lg font-bold">Documentos legais</h1>
          <p class="text-xs text-muted-foreground">
            Termos de Uso, Política de Privacidade e Cookies — versionados.
            Publicar dispara re-aceite de toda a base.
          </p>
        </div>
      </header>

      {errMsg && (
        <div class="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="size-4" /> {errMsg}
        </div>
      )}

      {/* Disponibilidade (rollout) */}
      <section class="rounded-md border border-border bg-card/60 p-4">
        <div class="mb-1 flex items-center gap-2">
          <Users className="size-4 text-violet-500" />
          <h2 class="text-sm font-bold">Disponibilidade</h2>
        </div>
        <p class="mb-3 text-xs text-muted-foreground">
          Controla pra quem o aceite e o banner de cookies aparecem. Use "Lista
          restrita" pra testar só com algumas contas antes de abrir pra todos.
        </p>
        <div class="flex flex-wrap gap-1">
          {(
            [
              ["off", "Desligado"],
              ["allowlist", "Lista restrita"],
              ["all", "Todos"]
            ] as const
          ).map(([m, label]) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setRolloutMode(m);
                void saveRollout(m);
              }}
              disabled={savingRollout}
              class={
                "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 " +
                (rolloutMode === m
                  ? "border-violet-500 bg-violet-500/15 text-violet-700 dark:text-violet-300"
                  : "border-border hover:bg-accent")
              }
            >
              {label}
            </button>
          ))}
        </div>
        {rolloutMode === "allowlist" && (
          <div class="mt-3 space-y-2">
            <label class="block text-xs font-medium text-muted-foreground">
              E-mails autorizados (um por linha)
            </label>
            <textarea
              value={allowlistText}
              placeholder="fulano@email.com"
              onInput={(e) =>
                setAllowlistText((e.target as HTMLTextAreaElement).value)
              }
              rows={4}
              class="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
            />
            <button
              type="button"
              onClick={() => void saveRollout()}
              disabled={savingRollout}
              class="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
            >
              <Save className="size-3.5" /> Salvar lista
            </button>
          </div>
        )}
        {rolloutMsg && (
          <p class="mt-2 text-xs text-muted-foreground">{rolloutMsg}</p>
        )}
      </section>

      {/* Tabs por kind */}
      <div class="flex flex-wrap gap-1 border-b border-border">
        {KINDS.map((k) => (
          <button
            key={k.kind}
            type="button"
            onClick={() => setActiveKind(k.kind)}
            class={
              "rounded-t-md px-3 py-2 text-sm font-medium transition-colors " +
              (activeKind === k.kind
                ? "border-b-2 border-violet-500 text-violet-700 dark:text-violet-300"
                : "text-muted-foreground hover:text-foreground")
            }
          >
            {k.label}
          </button>
        ))}
      </div>

      <div class="grid gap-5 md:grid-cols-[220px_1fr]">
        {/* Lista de versões */}
        <aside class="space-y-2">
          <div class="flex items-center justify-between">
            <h2 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Versões
            </h2>
            <button
              type="button"
              onClick={() => void handleNewVersion()}
              disabled={busy}
              class="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium hover:bg-accent disabled:opacity-50"
            >
              <Plus className="size-3" /> Nova
            </button>
          </div>
          {versions.length === 0 && (
            <p class="text-xs text-muted-foreground">
              Nenhuma versão ainda. Crie a primeira com "Nova".
            </p>
          )}
          <ul class="space-y-1">
            {versions.map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(v.id)}
                  class={
                    "w-full rounded-md border px-2.5 py-2 text-left text-xs transition-colors " +
                    (selectedId === v.id
                      ? "border-violet-500 bg-violet-500/10"
                      : "border-border hover:bg-accent")
                  }
                >
                  <div class="flex items-center justify-between">
                    <span class="font-semibold">v{v.version}</span>
                    <span
                      class={
                        "rounded border px-1.5 py-0.5 text-[10px] font-medium " +
                        (STATUS_BADGE[v.status] ?? "")
                      }
                    >
                      {STATUS_LABEL[v.status] ?? v.status}
                    </span>
                  </div>
                  <div class="mt-1 truncate text-muted-foreground">{v.title}</div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Editor / leitura */}
        <section class="min-w-0 space-y-3">
          {!selected ? (
            <p class="text-sm text-muted-foreground">
              Selecione ou crie uma versão.
            </p>
          ) : (
            <>
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="flex items-center gap-2 text-xs text-muted-foreground">
                  <span
                    class={
                      "rounded border px-1.5 py-0.5 font-medium " +
                      (STATUS_BADGE[selected.status] ?? "")
                    }
                  >
                    {STATUS_LABEL[selected.status]}
                  </span>
                  <span>v{selected.version}</span>
                  {selected.contentHash && (
                    <span class="font-mono">
                      #{selected.contentHash.slice(0, 10)}
                    </span>
                  )}
                </div>
                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPreview((p) => !p)}
                    class="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-accent"
                  >
                    <Eye className="size-3.5" /> {preview ? "Editar" : "Prévia"}
                  </button>
                </div>
              </div>

              {!isDraft && (
                <p class="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  Versão {STATUS_LABEL[selected.status].toLowerCase()} (somente
                  leitura). Para alterar, clique em <strong>Nova</strong> para
                  criar um rascunho a partir do texto atual.
                </p>
              )}

              {preview ? (
                <div class="rounded-md border border-border bg-card p-4">
                  <LegalDocViewer markdown={isDraft ? content : selected.content} />
                </div>
              ) : (
                <div class="space-y-3">
                  <div>
                    <label class="mb-1 block text-xs font-medium text-muted-foreground">
                      Título
                    </label>
                    <input
                      type="text"
                      value={title}
                      disabled={!isDraft}
                      placeholder="Ex.: Termos de Uso"
                      onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
                      class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-60"
                    />
                  </div>
                  <div>
                    <label class="mb-1 block text-xs font-medium text-muted-foreground">
                      Conteúdo (markdown)
                    </label>
                    <textarea
                      value={content}
                      disabled={!isDraft}
                      placeholder="Conteúdo em markdown…"
                      onInput={(e) =>
                        setContent((e.target as HTMLTextAreaElement).value)
                      }
                      rows={22}
                      class="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs leading-relaxed disabled:opacity-60"
                    />
                  </div>
                  <div>
                    <label class="mb-1 block text-xs font-medium text-muted-foreground">
                      Resumo da versão (changelog, opcional)
                    </label>
                    <input
                      type="text"
                      value={summary}
                      disabled={!isDraft}
                      placeholder="Ex.: ajuste na cláusula de pagamentos"
                      onInput={(e) =>
                        setSummary((e.target as HTMLInputElement).value)
                      }
                      class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-60"
                    />
                  </div>
                </div>
              )}

              {isDraft && (
                <div class="flex flex-wrap items-center gap-2 border-t border-border pt-3">
                  <button
                    type="button"
                    onClick={() => void handleSaveDraft()}
                    disabled={busy}
                    class="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
                  >
                    <Save className="size-4" /> Salvar rascunho
                  </button>
                  <button
                    type="button"
                    onClick={() => void handlePublish()}
                    disabled={busy}
                    class="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
                  >
                    <Send className="size-4" /> Publicar
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete()}
                    disabled={busy}
                    class="ml-auto inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
                  >
                    <Trash2 className="size-4" /> Apagar rascunho
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
